import { GuidedTourDefinition, GuidedTourId } from '../models';
import { SCREEN_TOURS, SCREEN_TOUR_ROUTES } from './screen-tours.registry';

/**
 * Definições dos tours. Textos vivem no código (pt-BR), nunca vêm da API.
 * Os alvos (`data-tour`) estão nos hosts reais de sidebar, bottom bar e busca.
 */
export const GLOBAL_TOUR: GuidedTourDefinition = {
  id: 'global.v1',
  version: 1,
  steps: [
    {
      id: 'global.dashboard',
      targetId: 'global.dashboard',
      title: 'Sua operação em um só lugar',
      description: 'Acompanhe vendas, estoque e informações importantes da sua empresa.',
      requiredAnyOf: [{ module: 'dashboard', action: 'view' }]
    },
    {
      id: 'global.product-search',
      targetId: 'global.product-search',
      title: 'Encontre produtos rapidamente',
      description: 'Pesquise um produto pelo nome ou código sem sair da sua rotina.',
      requiredAnyOf: [{ module: 'products', action: 'view' }]
    },
    {
      id: 'global.catalog',
      targetId: 'global.catalog',
      title: 'Organize seus produtos',
      description: 'Cadastre produtos, categorias e acompanhe o que há em estoque.',
      requiredAnyOf: [
        { module: 'products', action: 'view' },
        { module: 'inventory', action: 'view' }
      ]
    },
    {
      id: 'global.sales',
      targetId: 'global.sales',
      title: 'Registre suas vendas',
      description: 'Quando uma venda é confirmada, o GextorPro conecta as informações da operação.',
      requiredAnyOf: [
        { module: 'sales', action: 'view' },
        { module: 'sales', action: 'create' }
      ]
    }
  ]
};

export const PRODUCTS_TOUR: GuidedTourDefinition = {
  id: 'products.v1',
  version: 1,
  steps: [
    {
      id: 'products.new',
      targetId: 'products.new',
      title: 'Cadastre seus produtos',
      description: 'Cadastre o que você vende. Informe os dados necessários para a sua operação.',
      requiredAnyOf: [{ module: 'products', action: 'create' }]
    },
    {
      id: 'products.categories',
      targetId: 'products.categories',
      title: 'Organize por categorias',
      description: 'Organize os produtos para facilitar buscas e relatórios.',
      requiredAnyOf: [{ module: 'products', action: 'view' }],
      supportedViewports: ['desktop'],
      prepare: { navigationDomain: 'catalog' }
    },
    {
      id: 'products.filters',
      targetId: 'products.filters',
      title: 'Encontre o que procura',
      description: 'Use os filtros para localizar produtos e organizar sua lista.',
      requiredAnyOf: [{ module: 'products', action: 'view' }]
    }
  ]
};

export const INVENTORY_TOUR: GuidedTourDefinition = {
  id: 'inventory.v1',
  version: 1,
  steps: [
    {
      id: 'inventory.guide',
      targetId: 'inventory.guide',
      title: 'Os passos do estoque',
      description: 'Produtos, saldo, vendas e contagens. O passo destacado é o seu agora.',
      requiredAnyOf: [{ module: 'inventory', action: 'view' }]
    },
    {
      id: 'inventory.add-stock',
      targetId: 'inventory.add-stock',
      title: 'Adicione estoque',
      description: 'Registre as entradas para manter o saldo dos seus produtos atualizado.',
      requiredAnyOf: [{ module: 'inventory', action: 'view' }]
    },
    {
      id: 'inventory.movements',
      targetId: 'inventory.movements',
      title: 'Acompanhe as movimentações',
      description: 'Acompanhe entradas, saídas e transferências conforme sua configuração.',
      requiredAnyOf: [{ module: 'inventory', action: 'view' }]
    }
  ]
};

export const SALES_TOUR: GuidedTourDefinition = {
  id: 'sales.v1',
  version: 1,
  steps: [
    {
      id: 'sales.cash',
      targetId: 'sales.cash',
      title: 'Seu caixa',
      description: 'Veja se o caixa está aberto e abra o turno por aqui, sem sair da venda.',
      requiredAnyOf: [{ module: 'sales', action: 'cash_view' }]
    },
    {
      id: 'sales.new',
      targetId: 'sales.new',
      title: 'Comece pela busca',
      description: 'Selecione os produtos e registre a venda.',
      requiredAnyOf: [{ module: 'sales', action: 'create' }]
    },
    {
      id: 'sales.finalize',
      targetId: 'sales.finalize',
      title: 'Finalize a venda',
      description: 'Revise antes de confirmar. O registro conecta as próximas etapas da operação.',
      requiredAnyOf: [{ module: 'sales', action: 'create' }]
    }
  ]
};

export const PURCHASES_TOUR: GuidedTourDefinition = {
  id: 'purchases.v1',
  version: 1,
  steps: [
    {
      id: 'purchases.new',
      targetId: 'purchases.new',
      title: 'Registre suas compras',
      description:
        'Registre o que chegou e acompanhe o impacto no estoque e no financeiro, quando configurado.',
      requiredAnyOf: [{ module: 'purchases', action: 'create' }]
    },
    {
      id: 'purchases.list',
      targetId: 'purchases.list',
      title: 'Acompanhe seus pedidos',
      description: 'Veja pedidos, aprovações, recebimentos e pagamentos em um só lugar.',
      requiredAnyOf: [{ module: 'purchases', action: 'view' }]
    }
  ]
};

export const FINANCIAL_TOUR: GuidedTourDefinition = {
  id: 'financial.v1',
  version: 1,
  steps: [
    {
      id: 'financial.view',
      targetId: 'financial.view',
      title: 'Escolha a visão',
      description: 'Alterne a visão financeira para analisar o período selecionado.',
      requiredAnyOf: [{ module: 'financial', action: 'view' }]
    },
    {
      id: 'financial.summary',
      targetId: 'financial.summary',
      title: 'Contas a receber e a pagar',
      description: 'Veja contas a receber e a pagar e acompanhe o saldo previsto.',
      requiredAnyOf: [{ module: 'financial', action: 'view' }]
    }
  ]
};

export const REPORTS_TOUR: GuidedTourDefinition = {
  id: 'reports.v1',
  version: 1,
  steps: [
    {
      id: 'reports.period',
      targetId: 'reports.period',
      title: 'Escolha o período',
      description: 'Acompanhe os resultados quando houver movimentações registradas.',
      requiredAnyOf: [{ module: 'reports', action: 'view' }]
    },
    {
      id: 'reports.filters',
      targetId: 'reports.filters',
      title: 'Refine a análise',
      description: 'Use os filtros para detalhar o que você quer acompanhar.',
      requiredAnyOf: [{ module: 'reports', action: 'view' }]
    }
  ]
};

const DEFINITIONS: Partial<Record<GuidedTourId, GuidedTourDefinition>> = {
  [GLOBAL_TOUR.id]: GLOBAL_TOUR,
  [PRODUCTS_TOUR.id]: PRODUCTS_TOUR,
  [INVENTORY_TOUR.id]: INVENTORY_TOUR,
  [SALES_TOUR.id]: SALES_TOUR,
  [PURCHASES_TOUR.id]: PURCHASES_TOUR,
  [FINANCIAL_TOUR.id]: FINANCIAL_TOUR,
  [REPORTS_TOUR.id]: REPORTS_TOUR,
  ...Object.fromEntries(SCREEN_TOURS.map((definition) => [definition.id, definition]))
};

/** Tour contextual disponível para a rota atual (apenas acionado pelo usuário). */
const CONTEXTUAL_ROUTES: Array<{ match: (path: string) => boolean; tourId: GuidedTourId }> = [
  { match: (path) => path === '/produtos', tourId: 'products.v1' },
  {
    match: (path) =>
      path === '/estoque' || path === '/estoque/produtos' || path === '/movimentacoes-de-estoque',
    tourId: 'inventory.v1'
  },
  { match: (path) => path === '/vendas', tourId: 'sales.v1' },
  { match: (path) => path === '/compras', tourId: 'purchases.v1' },
  {
    match: (path) => path === '/financeiro' || path === '/financeiro/titulos',
    tourId: 'financial.v1'
  },
  {
    match: (path) => path === '/relatorios' || path.startsWith('/relatorios/'),
    tourId: 'reports.v1'
  }
];

export function contextualTourForPath(url: string): GuidedTourId | null {
  const path = url.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
  return (
    [...CONTEXTUAL_ROUTES, ...SCREEN_TOUR_ROUTES].find((route) => route.match(path))?.tourId ?? null
  );
}

export function getGuidedTourDefinition(id: GuidedTourId): GuidedTourDefinition | null {
  return DEFINITIONS[id] ?? null;
}

const GLOBAL_TOUR_DOMAIN_ANCHORS: Record<string, string> = {
  dashboard: 'global.dashboard',
  catalog: 'global.catalog',
  sales: 'global.sales'
};

/** `data-tour` de um domínio de navegação (rail desktop e barra inferior mobile). */
export function globalTourAnchor(domainId: string): string | null {
  return GLOBAL_TOUR_DOMAIN_ANCHORS[domainId] ?? null;
}

const PANEL_ITEM_TOUR_ANCHORS: Record<string, string> = {
  '/categorias-de-produto': 'products.categories',
  '/estoque/contagens': 'inventory.counts'
};

/** `data-tour` de um destino do painel do menu lateral (desktop). */
export function panelItemTourAnchor(route: string): string | null {
  return PANEL_ITEM_TOUR_ANCHORS[route] ?? null;
}
